import {
  Icon,
} from "@shopify/polaris";
import {
  ImageIcon,
  GlobeIcon,
  StoreIcon,
  PersonIcon,
  ReceiptDollarIcon,
  ListBulletedIcon,
  NoteIcon,
  CashDollarIcon,
  OrderIcon,
  LayoutFooterIcon,
  ClockIcon,
  SettingsIcon,
  CodeIcon,
} from "@shopify/polaris-icons";
import {
  PlanFeatureKeys,
  requiredPlanForFeature,
} from "@planEntitlements";
import type { ThemeNavRequiredPlan } from "./getThemeNavItems";

function navPlan(
  key: (typeof PlanFeatureKeys)[keyof typeof PlanFeatureKeys],
): ThemeNavRequiredPlan {
  return requiredPlanForFeature(key) as ThemeNavRequiredPlan;
}
/**
 * An array of objects representing the different buttons available in the application's navigation bar.
 * Each button has an ID, name, action, element, and media property.
 * The ID is a unique string identifier for the button.
 * The name is a string representing the name of the button as it appears in the UI.
 * The action is a string representing the action that should be taken when the button is clicked.
 * The element is a string representing the component that should be displayed when the button is clicked.
 * The media property is a JSX element representing the icon that should be displayed on the button.
 * @typedef {Object} NavButton
 * @property {string} id - A unique string identifier for the button.
 * @property {string} name - The name of the button as it appears in the UI.
 * @property {string} action - The action that should be taken when the button is clicked.
 * @property {string} element - The component that should be displayed when the button is clicked.
 * @property {JSX.Element} media - The icon that should be displayed on the button.
 */

/**
 * An array of NavButton objects representing the different buttons available in the application's navigation bar.
 * @typedef {NavButton[]} NavButtonList
 */

/**
 * An array of NavButton objects representing the different buttons available in the application's navigation bar.
 * @type {NavButtonList}
 */
export default [
  {
    id: "btnBrand",
    name: "Branding",
    action: "open_brand",
    element: "BrandStyle",
    media: <Icon source={ImageIcon} />,
    helpText: "Logo, colors, fonts and page margins",
  },
  {
    id: "btnOverview",
    name: "Overview",
    action: "open_overview",
    element: "Overview",
    media: <Icon source={GlobeIcon} />,
    helpText: "Document title, order number, dates and payment details",
  },
  {
    id: "btnSupplier",
    name: "Shop detail",
    action: "open_supplier",
    element: "Supplier",
    media: <Icon source={StoreIcon} />,
    helpText: "Seller name, address, tax IDs and contact details",
    headerSwitch: {
      settingKey: "is_supplier_enabled",
      accessibilityLabel: "Show shop info",
    },
  },
  {
    id: "btnClient",
    name: "Shipping info",
    action: "open_client",
    element: "Client",
    media: <Icon source={PersonIcon} />,
    helpText: "Customer shipping name, address, phone and tax IDs",
    headerSwitch: {
      settingKey: "is_shipping_enabled",
      accessibilityLabel: "Show shipping",
    },
  },
  {
    id: "btnBilling",
    name: "Billing info",
    action: "open_billing",
    element: "Billing",
    media: <Icon source={ReceiptDollarIcon} />,
    helpText: "Customer billing name, address, phone and tax IDs",
    headerSwitch: {
      settingKey: "is_billing_enabled",
      accessibilityLabel: "Show billing",
    },
  },
  {
    id: "btnLineItem",
    name: "Line items",
    action: "open_line_items",
    element: "LineItem",
    media: <Icon source={ListBulletedIcon} />,
    helpText: "Columns, barcodes, description and sort order",
  },
  {
    id: "btnLineItemNotes",
    name: "Notes",
    action: "open_line_item_notes",
    element: "LineItemNotes",
    media: <Icon source={NoteIcon} />,
    helpText: "Order note and table note under line items",
  },
  {
    id: "btnLineItemPrices",
    name: "Prices",
    action: "open_line_item_prices",
    element: "LineItemPrices",
    media: <Icon source={CashDollarIcon} />,
    helpText: "Item price, discount, tax and item subtotal",
  },
  {
    id: "btnLineItemTotals",
    name: "Totals",
    action: "open_line_item_totals",
    element: "LineItemTotals",
    media: <Icon source={OrderIcon} />,
    helpText: "Order subtotal, shipping, tax and document totals",
  },
  {
    id: "btnFooter",
    name: "Footer",
    action: "open_footer",
    element: "Footer",
    media: <Icon source={LayoutFooterIcon} />,
    helpText: "Notes, QR code, social links and terms",
    headerSwitch: {
      settingKey: "show_footer",
      accessibilityLabel: "Show footer",
    },
  },
  {
    id: "btnTimelineInfo",
    name: "Timeline",
    action: "open_timeline_info",
    element: "TimelineInfo",
    media: <Icon source={ClockIcon} />,
    helpText: "Order activity timeline grouped by date",
    headerSwitch: {
      settingKey: "show_timeline",
      accessibilityLabel: "Show timeline",
    },
  },
  {
    id: "btnDocumentSettings",
    name: "General settings",
    action: "open_document_settings",
    element: "DocumentSettings",
    media: <Icon source={SettingsIcon} />,
    helpText: "Date format, currency and page attachments",
  },
  {
    id: "btnCustomCss",
    name: "Custom CSS",
    action: "open_custom_css",
    element: "CustomCss",
    media: <Icon source={CodeIcon} />,
    helpText: "Add CSS that applies after theme styles",
    requiredPlan: navPlan(PlanFeatureKeys.CUSTOM_CSS),
  },
];
